import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Tooltip } from '@material-ui/core';
import { Person, WarningRounded } from '@material-ui/icons';

import { ApiParams } from '../../../common/api/api';
import CartToggleButton from '../../../common/cart/CartToggleButton';
import TaskStatusDot from '../../../common/TaskStatusDot';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import {
  RenderColumnMenu,
  RenderOptions
} from '../../../common/trace-board/interfaces/TraceBoardColumn';
import { TraceBoardItem } from '../../../common/trace-board/interfaces/TraceBoardItem';
import TraceBoard from '../../../common/trace-board/TraceBoard';
import { TraceBoardContextState } from '../../../common/trace-board/TraceBoardContext';
import BoardColumnMenu from '../../tasks/board/BoardColumnMenu';
import { Task } from '../../tasks/Task.interface';
import { useWorkflowContext } from '../../workflow/Workflow.context';
import { getStateNodeByStatus } from '../../workflow/Workflow.hooks';
import {
  getProductEntityIdPrefix,
  getProductEntityName
} from '../Product.config';
import {
  useGetProductBoard,
  useGetProductBoardReferences,
  useProductViewParams
} from '../Product.hooks';
import {
  Product,
  ProductBoardData,
  ProductDesignInput,
  ProductDesignOutput,
  ProductDesignSpec,
  ProductUserNeed,
  ProductValidation,
  ProductVerification
} from '../Product.interface';
import useProductBoardUpdateEntities from './hooks/useProductBoardUpdateEntities';
import { useCartContext } from '../../../common/cart/CartContext';
import environment from '../../../environments';

const StyledSubContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px 12px;
  .project-reference {
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    border: 1px solid #fec400;
    color: #b18d13;
    background: #fdf7eb;
    padding: 3px 5px;
    border-radius: 4px;
    margin-bottom: 8px;
  }
  .assigned {
    display: flex;
    align-items: center;
    .icon-wrapper {
      display: flex;
      margin-right: 10px;
      height: 24px;
      width: 24px;
      border-radius: 12px;
      align-items: center;
      justify-content: center;
      background: #efefef;
      overflow: hidden;
      .icon {
        width: 18px;
        height: 18px;
      }
    }
  }
  .view-details {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 8px;
    .button {
      flex: 1;
    }
    .cart-button {
      margin-left: 6px;
    }
  }
`;

const StyledWarning = styled(WarningRounded)`
  color: #f47560;
  width: 20px;
  height: 20px;
  margin-right: 4px;
`;

export interface ProductBoardTraceProps {
  product: Product;
  apiParams?: ApiParams;
}

const ProductBoardTrace: React.FC<ProductBoardTraceProps> = ({
  product,
  apiParams
}) => {
  const { getWorkflow } = useWorkflowContext();
  const { processedEntities } = useCartContext();
  const traceBoardRef = useRef<TraceBoardContextState>(null!);
  const [productBoards, setProductBoards] = useState<ProductBoardData[]>([]);
  if (apiParams?.search === '') delete apiParams.search;
  const hasFilters = Object.keys(apiParams || {}).length > 0;

  const {
    createdProductUserNeed,
    updatedProductUserNeed,
    createdProductDesignInput,
    updatedProductDesignInput,
    createdProductDesignOutput,
    updatedProductDesignOutput,
    createdProductVerification,
    updatedProductVerification,
    createdProductValidation,
    updatedProductValidation,
    createdProductDesignSpec,
    updatedProductDesignSpec,
    handleAddItem,
    handleUpdateItem
  } = useProductBoardUpdateEntities(product.id);

  const {
    productBoard: loadedProductBoard,
    isLoading,
    getProductBoard
  } = useGetProductBoard(product.id);
  useEffect(() => {
    if (loadedProductBoard) {
      // remove the previous product board and add the newly loaded one
      setProductBoards(productBoards => {
        productBoards = productBoards.filter(p =>
          Boolean(p.referencedProductId)
        );
        return [...productBoards, loadedProductBoard];
      });
    }
  }, [loadedProductBoard]);
  useEffect(() => {
    getProductBoard(apiParams);
    traceBoardRef.current.setLoadingItem(undefined);
  }, [
    apiParams,
    // fetch new product board if any product entites were created or updated
    createdProductUserNeed,
    updatedProductUserNeed,
    createdProductDesignInput,
    updatedProductDesignInput,
    createdProductDesignOutput,
    updatedProductDesignOutput,
    createdProductVerification,
    updatedProductVerification,
    createdProductValidation,
    updatedProductValidation,
    createdProductDesignSpec,
    updatedProductDesignSpec,
    processedEntities,
    getProductBoard
  ]);
  const {
    productBoardReferences: loadedProductReferences,
    getProductBoardReferences
  } = useGetProductBoardReferences();
  useEffect(() => {
    if (loadedProductReferences) {
      // prevent duplicates
      setProductBoards(productBoards => [
        ...productBoards.filter(
          p =>
            !loadedProductReferences.some(
              r =>
                r.productId === p.productId &&
                r.referencedProductId === p.referencedProductId
            )
        ),
        ...loadedProductReferences
      ]);
    }
  }, [loadedProductReferences]);

  const renderColumnMenu = (
    { columnIndex, column }: RenderColumnMenu,
    onClose?: () => void,
    onRender?: () => void
  ) => {
    const taskEntities = column.items.map(item => item.data);
    return (
      <BoardColumnMenu
        taskEntities={taskEntities}
        onRender={onRender}
        onChange={() => {
          onClose?.();
          getProductBoard(apiParams);
          traceBoardRef.current.setLoadingItem(undefined);
        }}
      />
    );
  };

  const countLinkedItemsRequiresReview = (
    item: TraceBoardItem,
    columnIndex: number
  ): number => {
    switch (columnIndex) {
      case 0:
        return 0;
      case 1: {
        const designInput = item.data as ProductDesignInput;
        return columns[0].items
          .filter(item =>
            (item.data as ProductUserNeed).designInputs?.some(
              d => d.id === designInput.id
            )
          )
          .reduce((count, item) => {
            const userNeed = item.data as ProductUserNeed;
            const stateNode = getWorkflowState(userNeed.task);
            if (stateNode?.type !== 'final') {
              count++;
            }
            return count;
          }, 0);
      }
      case 2: {
        const designOutput = item.data as ProductDesignOutput;
        return columns[1].items
          .filter(item =>
            (item.data as ProductDesignInput).designOutputs?.some(
              d => d.id === designOutput.id
            )
          )
          .reduce((count, item) => {
            const designInput = item.data as ProductDesignInput;
            const stateNode = getWorkflowState(designInput.task);
            if (stateNode?.type !== 'final') {
              count++;
            }
            return countLinkedItemsRequiresReview(item, 1) + count;
          }, 0);
      }
      case 3: {
        const verification = item.data as ProductVerification;
        return columns[2].items
          .filter(item =>
            (item.data as ProductDesignOutput).verifications?.some(
              v => v.id === verification.id
            )
          )
          .reduce((count, item) => {
            const designOutput = item.data as ProductDesignOutput;
            const stateNode = getWorkflowState(designOutput.task);
            if (stateNode?.type !== 'final') {
              count++;
            }
            return countLinkedItemsRequiresReview(item, 2) + count;
          }, 0);
      }
      case 4: {
        const validation = item.data as ProductValidation;
        const userNeedCount = columns[0].items
          .filter(item =>
            (item.data as ProductUserNeed).validations?.some(
              u => u.id === validation.id
            )
          )
          .reduce((count, item) => {
            const userNeed = item.data as ProductUserNeed;
            const stateNode = getWorkflowState(userNeed.task);
            if (stateNode?.type !== 'final') {
              count++;
            }
            return count;
          }, 0);
        const designInputCount = columns[1].items
          .filter(item =>
            (item.data as ProductDesignInput).validations?.some(
              d => d.id === validation.id
            )
          )
          .reduce((count, item) => {
            const designInput = item.data as ProductDesignInput;
            const stateNode = getWorkflowState(designInput.task);
            if (stateNode?.type !== 'final') {
              count++;
            }
            return countLinkedItemsRequiresReview(item, 1) + count;
          }, 0);
        return userNeedCount + designInputCount;
      }
      case 5: {
        const designSpec = item.data as ProductDesignSpec;
        return columns[0].items
          .filter(item =>
            (item.data as ProductUserNeed).designSpecs?.some(
              u => u.id === designSpec.id
            )
          )
          .reduce((count, item) => {
            const userNeed = item.data as ProductUserNeed;
            const stateNode = getWorkflowState(userNeed.task);
            if (stateNode?.type !== 'final') {
              count++;
            }
            return count;
          }, 0);
      }
    }
    return 0;
    // return (
    //   item.connectors
    //     ?.filter(c => c.columnIndex === columnIndex - 1)
    //     .reduce((count, connector) => {
    //       const task: Task = connector.sourceItem.data.task;
    //       const stateNode = getWorkflowState(task);
    //       if (stateNode?.type !== 'final') {
    //         count++;
    //       }
    //       count += countLinkedItemsRequiresReview(
    //         connector.sourceItem,
    //         connector.columnIndex
    //       );
    //       return count;
    //     }, 0) || 0
    // );
  };

  const renderTitleIcon = ({
    item,
    isSelected,
    isCollapsed
  }: RenderOptions) => {
    if (!item.data?.task) return null;
    const task: Task = item.data.task;
    if (!isCollapsed) return null;
    return <TaskStatusDot task={task} />;
  };
  const renderTitleAccessory = ({
    columnIndex,
    item,
    isCollapsed
  }: RenderOptions) => {
    if (!item.data?.task) return null;
    const task: Task = item.data.task;
    if (isCollapsed) return null;
    // find an item in the row that is not in the final state
    const stateNode = getWorkflowState(task);
    const requiresReviewCount =
      stateNode?.type === 'final'
        ? countLinkedItemsRequiresReview(item, columnIndex)
        : 0;
    return (
      <>
        {requiresReviewCount ? (
          <Tooltip
            title={`${requiresReviewCount} upstream linked item(s) that requires review`}
            placement='top'
            arrow
          >
            <StyledWarning />
          </Tooltip>
        ) : null}
        <TaskStatusLabel size='small' task={task} />
      </>
    );
  };
  const renderSubContent = ({
    item,
    column,
    columnIndex,
    isSelected,
    isCollapsed
  }: RenderOptions) => {
    let path = '';
    if (!item.data) return null;
    const productId = item.data.productId;
    switch (columnIndex) {
      case 0:
        path = `/portal/products/${productId}/user-needs/${item.id}?from=trace`;
        break;
      case 1:
        path = `/portal/products/${productId}/design-inputs/${item.id}?from=trace`;
        break;
      case 2:
        path = `/portal/products/${productId}/design-outputs/${item.id}?from=trace`;
        break;
      case 3:
        path = `/portal/products/${productId}/verifications/${item.id}?from=trace`;
        break;
      case 4:
        path = `/portal/products/${productId}/validations/${item.id}?from=trace`;
        break;
      case 5:
        path = `/portal/products/${productId}/design-specs/${item.id}?from=trace`;
        break;
    }
    if (isCollapsed) return null;
    const task = item.data?.task as Task;

    return (
      <StyledSubContent>
        {productId !== product.id && (
          <div className='project-reference'>
            {
              productBoards.find(p => p.referencedProductId === productId)
                ?.referencedProductTitle
            }
          </div>
        )}
        {task?.user && (
          <div className='assigned'>
            <div className='icon-wrapper'>
              <Person className='icon' />
            </div>
            {task.user.firstName} {task.user.lastName}
          </div>
        )}
        <div className={`view-details${isSelected ? '' : ' hover-only'}`}>
          <Button
            variant='outlined'
            color='primary'
            className='button'
            component={Link}
            to={path}
          >
            View Details
          </Button>
          {item.data && <CartToggleButton taskEntity={item.data} />}
        </div>
      </StyledSubContent>
    );
  };
  const query: string = useProductViewParams(true);
  const { columns, lookupReferencedProducts } = useMemo(() => {
    let lookupReferencedProducts: {
      productId: number;
      referencedProductId: number;
    }[] = [];
    // wait till productBoards is finished loading to prevent display of
    // empty state message
    if (isLoading || productBoards?.length === 0)
      return { columns: [], lookupReferencedProducts: [] };
    const columns = [
      {
        title: getProductEntityName('userNeed'),
        headerLink: `./user-needs${query}`,
        renderColumnMenu,
        renderTitleIcon,
        renderTitleAccessory,
        renderSubContent,
        items: productBoards.reduce((results, productBoard) => {
          const userNeeds =
            productBoard?.userNeeds.map(userNeed => {
              lookupReferencedProducts.push(
                ...(userNeed.designInputs
                  ?.filter(d => d.productId !== userNeed.productId)
                  ?.map(d => ({
                    productId: userNeed.productId,
                    referencedProductId: d.productId
                  })) || [])
              );
              lookupReferencedProducts.push(
                ...(userNeed.validations
                  ?.filter(v => v.productId !== userNeed.productId)
                  ?.map(v => ({
                    productId: userNeed.productId,
                    referencedProductId: v.productId
                  })) || [])
              );
              return {
                id: userNeed.id,
                title: `${getProductEntityIdPrefix('userNeed')}-${userNeed.id}`,
                description: userNeed.title,
                externalReference: userNeed.productId !== product.id,
                data: userNeed
              };
            }) || [];
          results.push(...userNeeds);
          return results;
        }, [] as TraceBoardItem[])
      },
      {
        title: getProductEntityName('designInput'),
        headerLink: `./design-inputs${query}`,
        renderColumnMenu,
        renderTitleIcon,
        renderTitleAccessory,
        renderSubContent,
        items: productBoards.reduce((results, productBoard) => {
          const designInputs =
            productBoard?.designInputs.map(designInput => {
              lookupReferencedProducts.push(
                ...(designInput.designOutputs
                  ?.filter(d => d.productId !== designInput.productId)
                  ?.map(d => ({
                    productId: designInput.productId,
                    referencedProductId: d.productId
                  })) || [])
              );
              return {
                id: designInput.id,
                title: `${getProductEntityIdPrefix('designInput')}-${
                  designInput.id
                }`,
                description: designInput.title,
                externalReference: designInput.productId !== product.id,
                data: designInput
              };
            }) || [];
          results.push(...designInputs);
          return results;
        }, [] as TraceBoardItem[])
      },
      {
        title: getProductEntityName('designOutput'),
        headerLink: `./design-outputs${query}`,
        renderColumnMenu,
        renderTitleIcon,
        renderTitleAccessory,
        renderSubContent,
        items: productBoards.reduce((results, productBoard) => {
          const designOutputs =
            productBoard?.designOutputs.map(designOutput => {
              lookupReferencedProducts.push(
                ...(designOutput.verifications
                  ?.filter(v => v.productId !== designOutput.productId)
                  ?.map(v => ({
                    productId: designOutput.productId,
                    referencedProductId: v.productId
                  })) || [])
              );
              return {
                id: designOutput.id,
                title: `${getProductEntityIdPrefix('designOutput')}-${
                  designOutput.id
                }`,
                description: designOutput.title,
                externalReference: designOutput.productId !== product.id,
                data: designOutput
              };
            }) || [];
          results.push(...designOutputs);
          return results;
        }, [] as TraceBoardItem[])
      },
      {
        title: getProductEntityName('verification'),
        headerLink: `./verifications${query}`,
        renderColumnMenu,
        renderTitleIcon,
        renderTitleAccessory,
        renderSubContent,
        items: productBoards.reduce((results, productBoard) => {
          const verifications =
            productBoard?.verifications.map(verification => ({
              id: verification.id,
              title: `${getProductEntityIdPrefix('verification')}-${
                verification.id
              }`,
              description: verification.title,
              externalReference: verification.productId !== product.id,
              data: verification
            })) || [];
          results.push(...verifications);
          return results;
        }, [] as TraceBoardItem[])
      },
      {
        title: getProductEntityName('validation'),
        headerLink: `./validations${query}`,
        renderColumnMenu,
        renderTitleIcon,
        renderTitleAccessory,
        renderSubContent,
        items: productBoards.reduce((results, productBoard) => {
          const validations =
            productBoard?.validations.map(validation => ({
              id: validation.id,
              title: `${getProductEntityIdPrefix('validation')}-${
                validation.id
              }`,
              description: validation.title,
              externalReference: validation.productId !== product.id,
              data: validation
            })) || [];
          results.push(...validations);
          return results;
        }, [] as TraceBoardItem[])
      }
    ];
    if (environment.productConfig?.designSpec) {
      columns.push({
        title: getProductEntityName('designSpec'),
        headerLink: `./design-specs${query}`,
        renderColumnMenu,
        renderTitleIcon,
        renderTitleAccessory,
        renderSubContent,
        items: productBoards.reduce((results, productBoard) => {
          const designSpecs =
            productBoard?.designSpecs.map(designSpec => ({
              id: designSpec.id,
              title: `${getProductEntityIdPrefix('designSpec')}-${
                designSpec.id
              }`,
              description: designSpec.title,
              externalReference: designSpec.productId !== product.id,
              data: designSpec
            })) || [];
          results.push(...designSpecs);
          return results;
        }, [] as TraceBoardItem[])
      });
    }
    // dedupe reference products
    lookupReferencedProducts = lookupReferencedProducts
      .filter(
        ({ productId, referencedProductId }, index) =>
          lookupReferencedProducts.findIndex(
            r =>
              r.productId === productId &&
              r.referencedProductId === referencedProductId
          ) === index
      )
      // filter out any products already referenced (received from server)
      .filter(
        ({ productId, referencedProductId }) =>
          !productBoards.some(
            r =>
              r.productId === productId &&
              r.referencedProductId === referencedProductId
          )
      );
    return { columns, lookupReferencedProducts };
  }, [productBoards, query]);

  // get all
  useEffect(() => {
    if (lookupReferencedProducts.length > 0) {
      const { productId } = lookupReferencedProducts[0];
      getProductBoardReferences(
        productId,
        lookupReferencedProducts
          .filter(r => r.productId === productId)
          .map(r => r.referencedProductId),
        apiParams
      );
    }
  }, [apiParams, getProductBoardReferences, lookupReferencedProducts]);

  const getWorkflowState = (task: Task) => {
    return getStateNodeByStatus(task.status, getWorkflow(task));
  };
  return (
    <TraceBoard
      innerRef={traceBoardRef}
      heading={hasFilters ? 'Filtered Cards' : undefined}
      orphanedRowHeading={hasFilters ? 'Orphaned Cards from filter' : undefined}
      columns={columns}
      isLoading={isLoading}
      filterColumnItems={(primaryItem, sourceItem, column, columnIndex) => {
        const data = sourceItem?.data;
        switch (columnIndex) {
          case 1: {
            if (!data) return [];
            const userNeed = data as ProductUserNeed;
            return column.items.filter(item =>
              userNeed.designInputs?.some(d => d.id === item.id)
            );
          }
          case 2: {
            if (!data) return [];
            const designInput = data as ProductDesignInput;
            return column.items.filter(item =>
              designInput.designOutputs?.some(d => d.id === item.id)
            );
          }
          case 3: {
            if (!data) return [];
            const designOutput = data as ProductDesignOutput;
            return column.items.filter(item =>
              designOutput.verifications?.some(v => v.id === item.id)
            );
          }
          case 4: {
            if (!primaryItem.data) return [];
            const userNeed = primaryItem.data as ProductUserNeed;
            // get design inputs for the user needs
            const designInputs = (
              columns[1].items.map(i => i.data) as ProductDesignInput[]
            ).filter(d1 => userNeed.designInputs?.some(d2 => d2.id === d1.id));
            // get design outputs for the design inputs
            const designOutputs = (
              columns[2].items.map(i => i.data) as ProductDesignOutput[]
            ).filter(d1 =>
              designInputs?.some(inp =>
                inp.designOutputs?.some(d2 => d2.id === d1.id)
              )
            );
            return column.items
              .filter(
                item =>
                  // check if validation linked to user needs
                  userNeed.validations?.some(v => v.id === item.id) ||
                  // check if validation linked to design inputs
                  designInputs.some(d =>
                    d.validations?.some(v => v.id === item.id)
                  ) ||
                  // check if validation linked to design outputs
                  designOutputs.some(out =>
                    out.validations?.some(v => v.id === item.id)
                  )
              )
              .map(item => {
                item.disableTrace = true;
                return item;
              });
          }
          case 5: {
            if (!primaryItem.data) return [];
            const userNeed = primaryItem.data as ProductUserNeed;
            return column.items
              .filter(item => userNeed.designSpecs?.some(v => v.id === item.id))
              .map(item => {
                item.disableTrace = true;
                return item;
              });
          }
          default:
            return [];
        }
      }}
      filterOrphanedItems={(column, columnIndex) => {
        const productBoard = productBoards.find(p => !p.referencedProductId);
        if (!productBoard) return [];
        switch (columnIndex) {
          // design inputs
          case 1: {
            return column.items.filter(
              item =>
                (item.id === 'new' ||
                  item.data?.productId === productBoard.productId) &&
                !productBoard.userNeeds.some(u =>
                  u.designInputs?.some(d => d.id === item.id)
                )
            );
          }
          case 2: {
            // design outputs
            return column.items.filter(item => {
              // only get items in current product board
              if (
                item.id !== 'new' &&
                item.data?.productId !== productBoard.productId
              ) {
                return false;
              }
              // get all related designInputs
              const designInputs = productBoard.designInputs.filter(
                designInput =>
                  designInput.designOutputs?.some(
                    designOutput => designOutput.id === item.id
                  )
              );
              // see if any of the related designInputs does not have a userNeed
              return !productBoard.userNeeds.some(u =>
                u.designInputs?.some(ud =>
                  designInputs.some(d => d.id === ud.id)
                )
              );
            });
          }
          case 3: {
            // verification
            return column.items.filter(item => {
              // only get items in current product board
              if (
                item.id !== 'new' &&
                item.data?.productId !== productBoard.productId
              ) {
                return false;
              }
              // get all related designOutputs
              const designOutputs = productBoard.designOutputs.filter(
                designOutput =>
                  designOutput.verifications?.some(
                    verification => verification.id === item.id
                  )
              );
              // get all related designInputs from the above designOutputs
              const designInputs = productBoard.designInputs.filter(
                designInput =>
                  designInput.designOutputs?.some(dd =>
                    designOutputs.some(d => d.id === dd.id)
                  )
              );
              // see if any of the related designInputs does not have a userNeed
              return !productBoard.userNeeds.some(u =>
                u.designInputs?.some(ud =>
                  designInputs.some(d => d.id === ud.id)
                )
              );
            });
          }
          case 4: {
            // validations
            return column.items.filter(
              item =>
                (item.id === 'new' ||
                  item.data?.productId === productBoard.productId) &&
                !productBoard.userNeeds.some(userNeed =>
                  userNeed.validations?.some(
                    validation => validation.id === item.id
                  )
                ) &&
                !productBoard.designInputs.some(designInput =>
                  designInput.validations?.some(
                    validation => validation.id === item.id
                  )
                ) &&
                !productBoard.designOutputs.some(designOutput =>
                  designOutput.validations?.some(
                    validation => validation.id === item.id
                  )
                )
            );
          }
          case 5: {
            // design specs
            return column.items.filter(
              item =>
                (item.id === 'new' ||
                  item.data?.productId === productBoard.productId) &&
                !productBoard.userNeeds.some(userNeed =>
                  userNeed.designSpecs?.some(
                    designSpec => designSpec.id === item.id
                  )
                )
            );
          }
          default:
            return [];
        }
      }}
      canEditItem={(sourceItem, column, columnIndex) => {
        const task = sourceItem.data?.task;
        if (!task) return false;
        const stateNode = getWorkflowState(task);
        if (!stateNode) return false;
        const allowedFields = stateNode.meta?.allowedFields || [];
        return allowedFields.includes('title');
      }}
      canLinkNewItem={(sourceItem, column, columnIndex) => {
        return columnIndex !== 3;
      }}
      onAddItem={handleAddItem}
      onUpdateItem={handleUpdateItem}
    />
  );
};

export default ProductBoardTrace;
